import React, { useContext, useEffect, useState, useCallback } from "react";
import NavigationBar from "../../components/element/NavigationBar";
import FBOHeaderBar from "../../components/element/FBOHeaderBar";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@mui/material";
import { PAGINATION_PAGE_SIZE, AIRCRAFT_TABS } from "../../utils/constants";
import AddNewLandingFeeDialog from "../../components/element/AddNewLandingFeeDialog";
import useFBOApi from "../../services/api/useFBOApi";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  CaretLeftOutlined,
  CaretRightOutlined,
  CloseOutlined,
} from "@ant-design/icons";
import FBOLandingFeeTable from "./Tables/FBOLandingFeeTable";
import PrivateAircraftTable from "./Tables/PrivateAircraftTable";
import { StorageContext } from "../../services/StorageProvider";
import SnackbarAlert from "../../components/element/SnackbarAlert";
import ROUTE_PATHS from "../../utils/routePaths";
import ViewLandingFeeDataFilter from "../../components/element/ViewLandingFeeDataFilter";
import CommonInputField from "../../components/element/CommonInputField";
import dayjs from "dayjs";

const FBOLandingFee = () => {
  const navigate = useNavigate();
  const [showAddNewLandingFeeDialog, setShowAddNewLandingFeeDialog] =
    useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const pageSize = PAGINATION_PAGE_SIZE;
  const { fetchLandingFeeData, addLandingFeeData, searchLandingFeeByTripId } =
    useFBOApi();
  const { fboUserProfileData, airportListData, aircraftListData } =
    useContext(StorageContext);
  const [filteredAirportListData, setFilteredAirportListData] = useState([]);
  const [actionCount, setActionCount] = useState(0);
  const [activeTab, setActiveTab] = useState(AIRCRAFT_TABS.COMMERCIAL);

  // Private Aircraft filter states
  const [showPrivateFilters, setShowPrivateFilters] = useState(false);
  const [privateFilterRegistrationNumber, setPrivateFilterRegistrationNumber] =
    useState("");
  const [privateFilterLandingDate, setPrivateFilterLandingDate] =
    useState(null);
  const [privateFilterDestinationAirport, setPrivateFilterDestinationAirport] =
    useState(null);
  const [privateSearchTripId, setPrivateSearchTripId] = useState("");
  const [privateIsSearchMode, setPrivateIsSearchMode] = useState(false);
  const [privateSearchResults, setPrivateSearchResults] = useState(null);

  // Commercial Aircraft filter states
  const [showCommercialFilters, setShowCommercialFilters] = useState(false);
  const [
    commercialFilterRegistrationNumber,
    setCommercialFilterRegistrationNumber,
  ] = useState("");
  const [commercialFilterLandingDate, setCommercialFilterLandingDate] =
    useState(null);
  const [
    commercialFilterDestinationAirport,
    setCommercialFilterDestinationAirport,
  ] = useState(null);
  const [commercialSearchFlightNo, setCommercialSearchFlightNo] = useState("");
  const [commercialIsSearchMode, setCommercialIsSearchMode] = useState(false);
  const [commercialSearchResults, setCommercialSearchResults] = useState(null);

  // Reset to first page when component mounts
  useEffect(() => {
    setCurrentPage(0);
  }, []);

  // Reset to first page when switching tabs
  useEffect(() => {
    setCurrentPage(0);
  }, [activeTab]);

  useEffect(() => {
    const filteredAirports = airportListData?.filter((airport) =>
      airport.terminals.some(
        (terminal) =>
          terminal.fboId === fboUserProfileData?.associations[0]?.fboId
      )
    );
    setFilteredAirportListData(filteredAirports);
  }, [airportListData, fboUserProfileData]);

  const {
    data: landingFeeData,
    error,
    isLoading,
  } = useQuery({
    queryKey: [
      "fbo-landing-fee-list",
      currentPage,
      activeTab === AIRCRAFT_TABS.PRIVATE
        ? privateFilterRegistrationNumber
        : commercialFilterRegistrationNumber,
      activeTab === AIRCRAFT_TABS.PRIVATE
        ? privateFilterLandingDate
        : commercialFilterLandingDate,
      activeTab === AIRCRAFT_TABS.PRIVATE
        ? privateFilterDestinationAirport
        : commercialFilterDestinationAirport,
      actionCount,
      activeTab,
    ],
    queryFn: () => {
      const params = {
        page: currentPage,
        size: pageSize,
      };

      const filterRegistrationNumber =
        activeTab === AIRCRAFT_TABS.PRIVATE
          ? privateFilterRegistrationNumber
          : commercialFilterRegistrationNumber;
      const filterLandingDate =
        activeTab === AIRCRAFT_TABS.PRIVATE
          ? privateFilterLandingDate
          : commercialFilterLandingDate;
      const filterDestinationAirport =
        activeTab === AIRCRAFT_TABS.PRIVATE
          ? privateFilterDestinationAirport
          : commercialFilterDestinationAirport;

      if (filterRegistrationNumber !== "") {
        params.aircraftRegistrationNumber = filterRegistrationNumber;
      }
      if (filterLandingDate) {
        params.arrivalDate = dayjs(filterLandingDate).format("YYYY-MM-DD");
      }
      if (filterDestinationAirport) {
        params.departureAirportId = filterDestinationAirport?.id;
      }

      // Add isCommercialFlight parameter based on active tab
      params.isCommercialFlight = activeTab === AIRCRAFT_TABS.COMMERCIAL;

      return fetchLandingFeeData(params);
    },
    retry: false,
    refetchOnMount: true,
    staleTime: 0,
    enabled:
      activeTab === AIRCRAFT_TABS.PRIVATE ? !privateIsSearchMode : !commercialIsSearchMode,
  });

  const {
    data: searchData,
    error: searchError,
    isLoading: isSearchLoading,
  } = useQuery({
    queryKey: [
      "fbo-landing-fee-search",
      activeTab === AIRCRAFT_TABS.PRIVATE ? privateSearchTripId : commercialSearchFlightNo,
      activeTab,
    ],
    queryFn: () => {
      const searchTerm =
        activeTab === AIRCRAFT_TABS.PRIVATE
          ? privateSearchTripId
          : commercialSearchFlightNo;
      const isCommercialFlight = activeTab === AIRCRAFT_TABS.COMMERCIAL;
      return searchLandingFeeByTripId(searchTerm, isCommercialFlight);
    },
    retry: false,
    enabled:
      activeTab === AIRCRAFT_TABS.PRIVATE
        ? privateIsSearchMode && privateSearchTripId.length > 3
        : commercialIsSearchMode && commercialSearchFlightNo.length > 3,
  });

  const addFBOLandingFeeMutation = useMutation({
    mutationFn: ({ data }) => addLandingFeeData(data),
    onSuccess: () => {
      setActionCount(actionCount + 1);
      setShowAddNewLandingFeeDialog(false);
    },
  });

  const handleAddRowClick = () => {
    setShowAddNewLandingFeeDialog(true);
  };
  const handleAddCPClick = () => {
    navigate(ROUTE_PATHS.CREATE_FLIGHT_PLAN);
  }

  const handleAddFBOLandingFee = (landingFeeData) => {
    addFBOLandingFeeMutation.mutate({
      data: landingFeeData,
    });
  };

  const handleRefreshData = () => {
    setActionCount(actionCount + 1);
  };

  useEffect(() => {
    if (
      landingFeeData?.data?.totalPages !== undefined &&
      currentPage >= landingFeeData.data.totalPages
    ) {
      setCurrentPage(Math.max(0, landingFeeData.data.totalPages - 1));
    }
  }, [landingFeeData?.data?.totalPages, currentPage]);

  // Handle search results
  useEffect(() => {
    if (searchData?.data) {
      // Convert single search result to array format for table display
      const searchResultArray = [searchData.data];
      if (activeTab === AIRCRAFT_TABS.PRIVATE) {
        setPrivateSearchResults(searchResultArray);
      } else {
        setCommercialSearchResults(searchResultArray);
      }
    } else if (searchError) {
      if (activeTab === AIRCRAFT_TABS.PRIVATE) {
        setPrivateSearchResults([]);
      } else {
        setCommercialSearchResults([]);
      }
    }
  }, [searchData, searchError, activeTab]);

  const handleApplyFilter = (
    registrationNumber,
    landingDate,
    destinationAirport
  ) => {
    if (activeTab === AIRCRAFT_TABS.PRIVATE) {
      setPrivateFilterRegistrationNumber(registrationNumber);
      setPrivateFilterLandingDate(landingDate);
      setPrivateFilterDestinationAirport(destinationAirport);
    } else {
      setCommercialFilterRegistrationNumber(registrationNumber);
      setCommercialFilterLandingDate(landingDate);
      setCommercialFilterDestinationAirport(destinationAirport);
    }
    setCurrentPage(0);
  };

  const handleClearFilter = () => {
    if (activeTab === AIRCRAFT_TABS.PRIVATE) {
      setPrivateFilterRegistrationNumber("");
      setPrivateFilterLandingDate(null);
      setPrivateFilterDestinationAirport(null);
    } else {
      setCommercialFilterRegistrationNumber("");
      setCommercialFilterLandingDate(null);
      setCommercialFilterDestinationAirport(null);
    }
    setCurrentPage(0);
  };

  // Debounced search function for Private Aircraft
  const debouncedPrivateSearch = useCallback(
    (() => {
      let timeoutId;
      return (value) => {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => {
          if (value.length > 3) {
            setPrivateIsSearchMode(true);
            setPrivateSearchTripId(value);
          } else if (value.length === 0) {
            setPrivateIsSearchMode(false);
            setPrivateSearchTripId("");
            setPrivateSearchResults(null);
          }
        }, 500);
      };
    })(),
    []
  );

  // Debounced search function for Commercial Aircraft
  const debouncedCommercialSearch = useCallback(
    (() => {
      let timeoutId;
      return (value) => {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => {
          if (value.length > 3) {
            setCommercialIsSearchMode(true);
            setCommercialSearchFlightNo(value);
          } else if (value.length === 0) {
            setCommercialIsSearchMode(false);
            setCommercialSearchFlightNo("");
            setCommercialSearchResults(null);
          }
        }, 500);
      };
    })(),
    []
  );

  const handleSearchChange = (e) => {
    const value = e.target.value;
    if (activeTab === AIRCRAFT_TABS.PRIVATE) {
      setPrivateSearchTripId(value);
      debouncedPrivateSearch(value);
    } else {
      setCommercialSearchFlightNo(value);
      debouncedCommercialSearch(value);
    }
  };

  const handleClearSearch = () => {
    if (activeTab === AIRCRAFT_TABS.PRIVATE) {
      setPrivateSearchTripId("");
      setPrivateIsSearchMode(false);
      setPrivateSearchResults(null);
    } else {
      setCommercialSearchFlightNo("");
      setCommercialIsSearchMode(false);
      setCommercialSearchResults(null);
    }
  };

  const handleNextPage = () => {
    if (currentPage + 1 < landingFeeData?.data?.totalPages) {
      setCurrentPage((prev) => prev + 1);
    }
  };

  const handlePreviousPage = () => {
    if (currentPage > 0) {
      setCurrentPage((prev) => prev - 1);
    }
  };

  return (
    <div>
      {addFBOLandingFeeMutation.isError ? (
        <SnackbarAlert type={"error"}>
          {addFBOLandingFeeMutation.error.message}
        </SnackbarAlert>
      ) : (
        ""
      )}
      <NavigationBar />
      <FBOHeaderBar
        title={"Landing Fee Data"}
        backContent={
          <div className="navigation-header">
            <Link to={ROUTE_PATHS.FBO_HOME} className="navigation-header-link">
              Home/
            </Link>
            <Link
              to={ROUTE_PATHS.LANDING_FEE_HOME}
              className="navigation-header-link"
            >
              Landing Fee/
            </Link>
            View Data
          </div>
        }
      />
      <div className="fbo-landing-fee-button-container">
        {/* Tab Navigation */}
        <div className="fbo-landing-fee-tabs">
          <Button
            variant="text"
            className={`fbo-landing-fee-tab ${
              activeTab === AIRCRAFT_TABS.PRIVATE ? "active" : ""
            }`}
            onClick={() => setActiveTab("private")}
            sx={{
              minWidth: "auto",
              width: "auto",
              textTransform: "none",
              boxShadow: "none",
              "&:hover": {
                boxShadow: "none",
              },
            }}
          >
            Private Aircraft
          </Button>
          <Button
            variant="text"
            className={`fbo-landing-fee-tab ${
              activeTab === AIRCRAFT_TABS.COMMERCIAL ? "active" : ""
            }`}
            onClick={() => setActiveTab("commercial")}
            sx={{
              minWidth: "auto",
              width: "auto",
              textTransform: "none",
              boxShadow: "none",
              "&:hover": {
                boxShadow: "none",
              },
            }}
          >
            Commercial Aircraft
          </Button>
        </div>

        <div className="fbo-landing-fee-action-buttons">
          <Button
            variant="contained"
            color={
              (activeTab === AIRCRAFT_TABS.PRIVATE && showPrivateFilters) ||
              (activeTab === AIRCRAFT_TABS.COMMERCIAL && showCommercialFilters)
                ? "send"
                : "disableGray"
            }
            onClick={() => {
              if (activeTab === AIRCRAFT_TABS.PRIVATE) {
                setShowPrivateFilters(!showPrivateFilters);
              } else {
                setShowCommercialFilters(!showCommercialFilters);
              }
            }}
          >
            Filters
          </Button>
          {activeTab === AIRCRAFT_TABS.PRIVATE ? (
            <Button
              variant="contained"
              color="secondary"
              onClick={handleAddRowClick}
            >
              Add Trip
            </Button>
          ) : (
            <Button
              variant="contained"
              color="secondary"
              onClick={handleAddCPClick}
            >
              Add Flight Roster
            </Button>
          )}
        </div>
      </div>
      {activeTab === AIRCRAFT_TABS.PRIVATE && showPrivateFilters && (
        <ViewLandingFeeDataFilter
          onClickClear={handleClearFilter}
          onClickApply={handleApplyFilter}
        />
      )}
      {activeTab === AIRCRAFT_TABS.COMMERCIAL && showCommercialFilters && (
        <ViewLandingFeeDataFilter
          onClickClear={handleClearFilter}
          onClickApply={handleApplyFilter}
        />
      )}
      <div className="fbo-landing-fee-search-container">
        <div className="fbo-landing-fee-search-field">
          <div className="fbo-landing-fee-search-input-wrapper">
            <CommonInputField
              id={activeTab === AIRCRAFT_TABS.PRIVATE ? "tripIdSearch" : "flightNoSearch"}
              label={
                activeTab === AIRCRAFT_TABS.PRIVATE
                  ? "Search by Trip ID"
                  : "Search by Flight No"
              }
              type="text"
              value={
                activeTab === AIRCRAFT_TABS.PRIVATE
                  ? privateSearchTripId
                  : commercialSearchFlightNo
              }
              onChange={handleSearchChange}
              placeholder="Enter here"
              maxLength={15}
            />
            {(activeTab === AIRCRAFT_TABS.PRIVATE
              ? privateSearchTripId
              : commercialSearchFlightNo) && (
              <button
                className="fbo-landing-fee-search-clear-icon"
                onClick={handleClearSearch}
                type="button"
              >
                <CloseOutlined />
              </button>
            )}
          </div>
        </div>
      </div>
      {activeTab === AIRCRAFT_TABS.PRIVATE ? (
        <FBOLandingFeeTable
          landingFeeData={
            privateIsSearchMode
              ? privateSearchResults
              : landingFeeData?.data?.content
          }
          isLoading={privateIsSearchMode ? isSearchLoading : isLoading}
          error={privateIsSearchMode ? searchError : error}
          onRefresh={handleRefreshData}
        />
      ) : (
        <PrivateAircraftTable
          landingFeeData={
            commercialIsSearchMode
              ? commercialSearchResults
              : landingFeeData?.data?.content
          }
          isLoading={commercialIsSearchMode ? isSearchLoading : isLoading}
          error={commercialIsSearchMode ? searchError : error}
          onRefresh={handleRefreshData}
        />
      )}
      {!(activeTab === AIRCRAFT_TABS.PRIVATE
        ? privateIsSearchMode
        : commercialIsSearchMode) &&
        landingFeeData?.data?.content &&
        landingFeeData?.data?.content.length > 0 && (
          <div className="pagination-container">
            <div className="pagination-content">
              Page {currentPage + 1} of {landingFeeData?.data?.totalPages}
            </div>
            <button
              className="pagination-button"
              onClick={handlePreviousPage}
              disabled={currentPage === 0}
            >
              <CaretLeftOutlined />
            </button>
            <button
              className="pagination-button"
              onClick={handleNextPage}
              disabled={currentPage + 1 === landingFeeData?.data?.totalPages}
            >
              <CaretRightOutlined />
            </button>
          </div>
        )}
      <AddNewLandingFeeDialog
        open={showAddNewLandingFeeDialog}
        onClose={() => setShowAddNewLandingFeeDialog(false)}
        isLoading={addFBOLandingFeeMutation?.isPending}
        onClickSave={handleAddFBOLandingFee}
        fboUserData={fboUserProfileData}
        aircraftData={aircraftListData?.data}
        originAirportList={airportListData}
        destinationAirportList={filteredAirportListData}
      />
    </div>
  );
};

export default FBOLandingFee;
